import { useCallback, useState } from "react"
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons"
import { Button, Form, Input, Modal, Radio, Upload, message } from "antd"
import type { UploadFile } from "antd/es/upload/interface"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { submitFeedbackFunc } from "../../App"
import { useAuth } from "../../authContext"
import { getFirebaseStorage } from "../../firebaseStorage"

const MAX_BYTES = 10 * 1024 * 1024
const ALLOWED_TYPES = new Set([ "image/jpeg", "image/png", "image/webp" ])

const extForType = (type: string): string => {
  if (type === "image/png") return "png"
  if (type === "image/webp") return "webp"
  return "jpg"
}

const validateScreenshotFile = (file: File): string | null => {
  if (!ALLOWED_TYPES.has(file.type)) return "Use JPEG, PNG, or WebP"
  if (file.size > MAX_BYTES) return "Image must be under 10 MB"
  return null
}

const uploadFeedbackScreenshot = async (file: File): Promise<string> => {
  const err = validateScreenshotFile(file)
  if (err) throw new Error(err)

  const storage = getFirebaseStorage()
  const ext = extForType(file.type)
  const id = crypto.randomUUID()
  const path = `feedback/${id}/${Date.now()}.${ext}`
  const objRef = ref(storage, path)
  await uploadBytes(objRef, file, { contentType: file.type })
  return getDownloadURL(objRef)
}

type FeedbackFormValues = {
  kind: "bug" | "feature"
  title: string
  description: string
}

type FeedbackButtonProps = {
  pagePath: string
}

const FeedbackButton = ({ pagePath }: FeedbackButtonProps) => {
  const { email, displayName } = useAuth()
  const [ open, setOpen ] = useState(false)
  const [ submitting, setSubmitting ] = useState(false)
  const [ screenshotFile, setScreenshotFile ] = useState<File | null>(null)
  const [ screenshotPreview, setScreenshotPreview ] = useState<string | null>(null)
  const [ form ] = Form.useForm<FeedbackFormValues>()

  const resetForm = useCallback(() => {
    form.resetFields()
    form.setFieldsValue({ kind: "bug" })
    setScreenshotFile(null)
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview)
    setScreenshotPreview(null)
  }, [ form, screenshotPreview ])

  const closeModal = useCallback(() => {
    setOpen(false)
    resetForm()
  }, [ resetForm ])

  const clearScreenshot = () => {
    setScreenshotFile(null)
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview)
    setScreenshotPreview(null)
  }

  const handleSubmit = async () => {
    let values: FeedbackFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    setSubmitting(true)
    const hide = message.loading("Submitting feedback…", 0)
    try {
      let screenshotUrl: string | undefined
      if (screenshotFile) {
        screenshotUrl = await uploadFeedbackScreenshot(screenshotFile)
      }

      const res = await submitFeedbackFunc({
        kind: values.kind,
        title: values.title.trim(),
        description: values.description.trim(),
        pageUrl: pagePath,
        screenshotUrl,
      })

      const data = res.data as { status?: string; message?: string; issueUrl?: string; issueNumber?: number }
      if (data.status === "error") {
        message.error(data.message || "Could not submit feedback")
        return
      }

      const issueLabel = data.issueNumber ? ` #${data.issueNumber}` : ""
      message.success(
        <span>
          Thanks — issue{issueLabel} created.{" "}
          {data.issueUrl ? (
            <a href={data.issueUrl} target="_blank" rel="noopener noreferrer">Open in GitHub</a>
          ) : null}
        </span>,
        8,
      )
      closeModal()
    } catch (err) {
      console.error("submitFeedback:", err)
      message.error((err as Error).message || "Could not submit feedback")
    } finally {
      hide()
      setSubmitting(false)
    }
  }

  const reporter = [ displayName?.trim(), email?.trim() ].filter(Boolean).join(" · ")

  return (
    <>
      <button
        type="button"
        className="side-nav-feedback"
        onClick={() => {
          resetForm()
          setOpen(true)
        }}
      >
        Report bug / suggest feature
      </button>

      <Modal
        title="Report bug / suggest feature"
        open={open}
        onCancel={closeModal}
        onOk={handleSubmit}
        okText="Submit"
        confirmLoading={submitting}
        width={560}
        destroyOnClose
      >
        <p style={{ fontSize: "var(--fs-cell)", color: "var(--muted)", margin: "0 0 12px" }}>
          Creates a GitHub issue for the Viridian team. No GitHub account needed — we attach your Viridian sign-in.
        </p>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ kind: "bug" }}
        >
          <Form.Item name="kind" label="Type" rules={[ { required: true } ]}>
            <Radio.Group>
              <Radio.Button value="bug">Bug</Radio.Button>
              <Radio.Button value="feature">Feature</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: "Title is required" },
              { max: 200, message: "Max 200 characters" },
            ]}
          >
            <Input placeholder="Short summary" maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: "Description is required" },
              { max: 8000, message: "Max 8000 characters" },
            ]}
          >
            <Input.TextArea rows={5} placeholder="What happened? What would you like changed?" maxLength={8000} showCount />
          </Form.Item>
          <Form.Item label="Page">
            <Input value={pagePath} readOnly />
          </Form.Item>
          {reporter ? (
            <Form.Item label="Reporter">
              <Input value={reporter} readOnly />
            </Form.Item>
          ) : null}
          <Form.Item label="Screenshot (optional)">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Upload
                accept="image/jpeg,image/png,image/webp"
                showUploadList={false}
                beforeUpload={() => false}
                fileList={screenshotFile ? [ { uid: "1", name: screenshotFile.name } as UploadFile ] : []}
                onChange={info => {
                  const file = info.file.originFileObj as File | undefined
                  if (!file) return
                  const err = validateScreenshotFile(file)
                  if (err) {
                    message.error(err)
                    return
                  }
                  if (screenshotPreview) URL.revokeObjectURL(screenshotPreview)
                  setScreenshotFile(file)
                  setScreenshotPreview(URL.createObjectURL(file))
                }}
              >
                <Button icon={<UploadOutlined />} size="small">Choose image</Button>
              </Upload>
              {screenshotPreview ? (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 4, border: "1px solid var(--line)" }}
                  />
                  <Button size="small" icon={<DeleteOutlined />} onClick={clearScreenshot} aria-label="Remove screenshot" />
                </div>
              ) : null}
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default FeedbackButton
